import { Vehicle } from '@/types';
import jsPDF from 'jspdf';

const formatDateFR = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
};

const CATEGORY_LABELS: Record<string, string> = {
  camion: 'Camions',
  'materiel-industriel': 'Matériel industriel',
  utilitaire: 'Utilitaires',
  remorque: 'Remorques',
  autre: 'Autres',
};

const TRANSMISSION_LABELS: Record<string, string> = {
  manuelle: 'Manuelle',
  automatique: 'Auto',
  semi_automatique: 'Semi-auto',
};

const CONDITION_LABELS: Record<string, string> = {
  excellent: 'Excellent',
  bon: 'Bon',
  correct: 'Correct',
  usage: 'Usagé',
};

const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    // Use fetch to avoid CORS canvas tainting issues
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // Resize via canvas for smaller PDF
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const maxW = 120;
            const maxH = 80;
            const scale = Math.min(maxW / img.width, maxH / img.height);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(dataUrl); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.6));
          } catch { resolve(dataUrl); }
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const addHeader = (doc: jsPDF, pageWidth: number, margin: number): number => {
  let y = 20;
  doc.setFontSize(22);
  doc.setTextColor(0, 102, 204);
  doc.text('Trucks 52 SARL', margin, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text("Vente de véhicules industriels d'occasion", margin, y);
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text("2 Place de l'Église, Recourt - 52140 Val-de-Meuse", margin, y);
  doc.text('Tél: 06 08 24 85 29 | Email: trucks.52@orange.fr', pageWidth - margin, y, { align: 'right' });
  y += 5;
  doc.text('SIRET: 508 009 495 00013', margin, y);
  doc.text('www.trucks52.fr', pageWidth - margin, y, { align: 'right' });
  y += 3;
  doc.setDrawColor(0, 102, 204);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;
  return y;
};

const addFooter = (doc: jsPDF, pageWidth: number, pageNum: number, totalPages: number) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setTextColor(150);
  doc.text(
    `Trucks 52 SARL - Catalogue véhicules - Généré le ${formatDateFR(new Date())} - Page ${pageNum}/${totalPages}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: 'center' }
  );
};

const ROW_HEIGHT = 22;
const THUMB_W = 28;
const THUMB_H = 18;

export const generateCatalogPDF = async (vehicles: Vehicle[]): Promise<void> => {
  // Pre-load all thumbnails
  const imageCache = new Map<string, string | null>();
  const imagePromises = vehicles.map(async (v) => {
    const url = v.images?.[0];
    if (url && !imageCache.has(url)) {
      const data = await loadImageAsBase64(url);
      imageCache.set(url, data);
    }
  });
  await Promise.all(imagePromises);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;

  let yPos = addHeader(doc, pageWidth, margin);

  // Title
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text('Catalogue des véhicules disponibles', margin, yPos);
  yPos += 6;
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`${vehicles.length} véhicule(s) en stock au ${formatDateFR(new Date())}`, margin, yPos);
  yPos += 10;

  // Group by category
  const grouped: Record<string, Vehicle[]> = {};
  vehicles.forEach(v => {
    const cat = v.category || 'autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(v);
  });

  const categoryOrder = ['camion', 'utilitaire', 'remorque', 'materiel-industriel', 'autre'];

  for (const cat of categoryOrder) {
    const list = grouped[cat];
    if (!list || list.length === 0) continue;

    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = addHeader(doc, pageWidth, margin);
    }

    // Category header
    doc.setFillColor(0, 102, 204);
    doc.rect(margin, yPos - 4, contentWidth, 8, 'F');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(`${CATEGORY_LABELS[cat] || cat} (${list.length})`, margin + 3, yPos + 1);
    yPos += 10;

    // Vehicle rows with thumbnails
    list.forEach((v, idx) => {
      if (yPos + ROW_HEIGHT > pageHeight - 15) {
        doc.addPage();
        yPos = addHeader(doc, pageWidth, margin);
      }

      // Alternate row bg
      if (idx % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, yPos - 2, contentWidth, ROW_HEIGHT, 'F');
      }

      // Thumbnail
      const imgUrl = v.images?.[0];
      const imgData = imgUrl ? imageCache.get(imgUrl) : null;
      const textX = margin + THUMB_W + 4;

      if (imgData) {
        try {
          doc.addImage(imgData, 'JPEG', margin + 1, yPos - 1, THUMB_W, THUMB_H);
        } catch {
          // Draw placeholder
          doc.setDrawColor(200);
          doc.setFillColor(230, 230, 230);
          doc.rect(margin + 1, yPos - 1, THUMB_W, THUMB_H, 'FD');
        }
      } else {
        doc.setDrawColor(200);
        doc.setFillColor(230, 230, 230);
        doc.rect(margin + 1, yPos - 1, THUMB_W, THUMB_H, 'FD');
        doc.setFontSize(5);
        doc.setTextColor(160);
        doc.text('Pas de photo', margin + 4, yPos + 8);
      }

      // Title (bold)
      doc.setFontSize(8);
      doc.setTextColor(20);
      const title = v.title.length > 45 ? v.title.substring(0, 43) + '...' : v.title;
      doc.text(title, textX, yPos + 3);

      // Line 2: specs
      doc.setFontSize(6.5);
      doc.setTextColor(80);
      const specs: string[] = [];
      if (v.year && v.year !== 'avant-1990') specs.push(`${v.year}`);
      if (v.mileage) specs.push(`${v.mileage.toLocaleString('fr-FR')} km`);
      if (v.fuelType) specs.push(v.fuelType);
      if (v.enginePower) specs.push(`${v.enginePower} ch`);
      if (v.transmission) specs.push(TRANSMISSION_LABELS[v.transmission] || v.transmission);
      if (v.maxWeight) specs.push(`PTAC ${v.maxWeight.toLocaleString('fr-FR')} kg`);
      doc.text(specs.join('  •  '), textX, yPos + 9);

      // Line 3: condition + extras
      const extras: string[] = [];
      if (v.generalCondition) extras.push(`État: ${CONDITION_LABELS[v.generalCondition] || v.generalCondition}`);
      if (v.axleConfiguration) extras.push(`Essieux: ${v.axleConfiguration}`);
      if (v.payloadCapacity) extras.push(`CU: ${v.payloadCapacity.toLocaleString('fr-FR')} kg`);
      if (extras.length > 0) {
        doc.text(extras.join('  •  '), textX, yPos + 14);
      }

      // Price (right-aligned)
      const price = v.priceTTC || v.price;
      doc.setFontSize(9);
      doc.setTextColor(0, 102, 204);
      doc.text(
        price ? `${price.toLocaleString('fr-FR')} €` : 'Sur demande',
        pageWidth - margin - 2,
        yPos + 3,
        { align: 'right' }
      );

      yPos += ROW_HEIGHT + 2;
    });

    yPos += 4;
  }

  // Add footers
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, pageWidth, i, totalPages);
  }

  doc.save(`Trucks52-Catalogue-${new Date().toISOString().slice(0, 10)}.pdf`);
};
